export type Hand = 'left' | 'right';
export type Target = 'head' | 'body';
export type PunchAction = 'straight' | 'hook' | 'bodyBlow' | 'uppercut' | 'super';
export type Action = PunchAction | 'blockStart' | 'blockEnd' | 'dodge';
export type Side = 'player' | 'opponent';
export type Outcome = 'clean' | 'blocked' | 'dodged';
export type BoutPhase = 'round' | 'rest' | 'over';
export type Winner = 'player' | 'opponent' | 'nobody';

const punchActions: readonly Action[] = ['straight', 'hook', 'bodyBlow', 'uppercut', 'super'];

export function isPunch(action: Action): action is PunchAction {
  return punchActions.includes(action);
}

export function other(side: Side): Side {
  return side === 'player' ? 'opponent' : 'player';
}

/** What the player (or the opponent's brain) asks for. Every input source produces only these. */
export interface FightCommand {
  readonly hand: Hand;
  readonly action: Action;
}

/** One punch's numbers, from its card. */
export interface PunchStats {
  readonly damage: number;
  readonly windupSeconds: number;
  readonly activeSeconds: number;
  readonly recoverySeconds: number;
  readonly hand: Hand;
  readonly action: PunchAction;
  readonly target: Target;
  /** A clean hit makes the target stagger: longer than the usual flinch, and he can do nothing. */
  readonly staggers: boolean;
  /** Stamina it takes to throw. */
  readonly staminaCost: number;
}

export function punchStats(stats: Partial<PunchStats> & Pick<PunchStats, 'damage' | 'windupSeconds' | 'activeSeconds' | 'recoverySeconds'>): PunchStats {
  const made: PunchStats = {
    hand: 'left',
    action: 'straight',
    target: 'head',
    staggers: false,
    staminaCost: 0,
    ...stats,
  };
  if (made.damage < 0) throw new RangeError('A punch cannot heal');
  if (made.windupSeconds <= 0 || made.activeSeconds <= 0 || made.recoverySeconds <= 0)
    throw new RangeError('Every punch phase must last longer than zero seconds');
  if (made.staminaCost < 0) throw new RangeError('A punch cannot give stamina');
  return made;
}

/** What one punch did when it arrived. */
export interface PunchReport {
  readonly attacker: Side;
  readonly punch: PunchStats;
  readonly outcome: Outcome;
  readonly damage: number;
}

/** What one fighter did in the bout. */
export class FighterStats {
  thrown = 0;
  /** His punches that landed clean. */
  landed = 0;
  /** The other one's punches he blocked. */
  blocked = 0;
  /** The other one's punches he dodged. */
  dodged = 0;
  damageDealt = 0;
}

export interface BoutResult {
  readonly winner: Winner;
  readonly byKnockout: boolean;
  /** The round the bout ended in. */
  readonly round: number;
  readonly player: FighterStats;
  readonly opponent: FighterStats;
}
